import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import AppContext from "context/Context";
import React, { useState, useContext, useEffect, useRef } from "react";
import { getAllDatabaseRecords, getDatabaseRecord } from "components/workflow/WorkflowHelper";
import { Row, Col, Container, Dropdown } from "react-bootstrap";
import logo from "assets/img/logo/mwanLogo.png";
import * as General from "common/general";

const EmanifestPreview = () => {
	const {
		config: { isDark, isRTL },
		setConfig,
	} = useContext(AppContext);
	const { id } = useParams();
	const { t, i18n } = useTranslation();
	const [emanifestInfo, setEmanifestInfo] = useState([]);
	const isFirstRender = useRef(true);

	// Get the preferred language - default to Arabic
	const preferredLanguage = localStorage.getItem("preferredLanguage") || "ar";
	const [language, setLanguage] = useState(preferredLanguage);

	// Force i18n and RTL settings on component mount - only runs once
	useEffect(() => {
		if (isFirstRender.current) {
			// First time visit - set default to Arabic if not already set
			if (!localStorage.getItem("preferredLanguage")) {
				localStorage.setItem("preferredLanguage", "ar");
			}

			// Set RTL direction in document
			document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
			document.body.dir = language === "ar" ? "rtl" : "ltr";

			// Force i18n language change
			i18n.changeLanguage(language);

			// Update AppContext
			setConfig({
				isDark,
				isRTL: language === "ar",
			});

			console.log("Initial language set to:", language, "isRTL:", language === "ar");
			localStorage.setItem("isRTL", language === "ar");
			// Mark first render as completed
			isFirstRender.current = false;

			// Force a full page reload on initial visit to ensure translations apply
			if (!sessionStorage.getItem("initialLoadComplete")) {
				sessionStorage.setItem("initialLoadComplete", "true");
				console.log("Forcing initial page reload to apply translations");
				window.location.reload();
			}
		}
	}, []);

	// Language switching function
	const changeLanguage = (lang) => {
		console.log("Changing language to:", lang);

		// Update state
		setLanguage(lang);

		// Update localStorage
		localStorage.setItem("preferredLanguage", lang);

		// Update RTL setting
		const isRTL = lang === "ar";

		// Update RTL in document
		document.documentElement.dir = isRTL ? "rtl" : "ltr";
		document.body.dir = isRTL ? "rtl" : "ltr";

		// Update i18n
		i18n.changeLanguage(lang);
		localStorage.setItem("isRTL", isRTL);
		// Update global context with the new RTL setting
		setConfig({
			isDark,
			isRTL,
		});

		// Force reload to apply all changes
		window.location.reload();
	};

	// Fetch data
	useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await General.GetPreviewEmanifest("tbl_emanifest_workflow", `generatedEmanifestID=eq.${id}`);
				const item = response?.data?.dataBaseContent?.[0];
				if (item) {
					const parsed = {
						...item,
						receivingWasteFacility: JSON.parse(item.receivingWasteFacility),
						requesterDetails: JSON.parse(item.requesterDetails),
						transporterRepresentative: JSON.parse(item.transporterRepresentative),
						truckDriver: JSON.parse(item.truckDriver),
						truckInfo: JSON.parse(item.truckInfo),
						wasteGeneratorDelegate: JSON.parse(item.wasteGeneratorDelegate),
						wasteTransporter: JSON.parse(item.wasteTransporter),
						waste: JSON.parse(item.waste),
					};
					setEmanifestInfo(parsed);
				} else {
					console.error("No data found for the given ID.");
				}
			} catch (error) {
				console.error("No record found", error);
			}
		};

		fetchData();
	}, [id]);

	return (
		<Container className="mb-2 mt-2" dir={language === "ar" ? "rtl" : "ltr"}>
			<div className="container-fluid px-0 py-2">
				{/* Desktop view */}
				<div className="d-none d-md-flex justify-content-between align-items-center position-relative">
					<div className="position-absolute" style={{ left: 0 }}>
						<Dropdown>
							<Dropdown.Toggle variant="outline-secondary" id="language-dropdown" size="sm">
								{language === "ar" ? "العربية" : "English"}
							</Dropdown.Toggle>
							<Dropdown.Menu align={"end"}>
								<Dropdown.Item onClick={() => changeLanguage("ar")} active={language === "ar"}>
									العربية
								</Dropdown.Item>
								<Dropdown.Item onClick={() => changeLanguage("en")} active={language === "en"}>
									English
								</Dropdown.Item>
							</Dropdown.Menu>
						</Dropdown>
					</div>

					<h2 className="text-center m-0" style={{ flex: 1, textAlign: "center" }}>
						{t("Electronic transport document")}
					</h2>

					<div className="position-absolute" style={{ right: 0 }}>
						<img src={localStorage.getItem("logo")} alt="Logo" style={{ width: "auto", maxHeight: "50px", maxWidth: "100%" }} />
					</div>
				</div>

				{/* Mobile view */}
				<div className="d-md-none" style={{ marginTop: 10 }}>
					<div className="d-flex justify-content-between align-items-center mb-2">
						<div className="position-absolute" style={{ left: 0 }}>
							<Dropdown>
								<Dropdown.Toggle id="language-dropdown-mobile" size="sm">
									{language === "ar" ? "ع" : "En"}
								</Dropdown.Toggle>
								<Dropdown.Menu align={"end"}>
									<Dropdown.Item onClick={() => changeLanguage("ar")} active={language === "ar"}>
										العربية
									</Dropdown.Item>
									<Dropdown.Item onClick={() => changeLanguage("en")} active={language === "en"}>
										English
									</Dropdown.Item>
								</Dropdown.Menu>
							</Dropdown>
						</div>

						<div className="position-absolute" style={{ right: 0 }}>
							<img src={localStorage.getItem("logo")} alt="Logo" style={{ width: "auto", maxHeight: "35px", maxWidth: "120%" }} />
						</div>
					</div>

					<div className="text-center" style={{ marginTop: 20 }}>
						<h4 className="m-0">{t("Electronic transport document")}</h4>
					</div>
				</div>
			</div>

			<div className="custom-panel-title">
				<div className="mb-2 card border">
					<div className="card-header bg-default">
						<span className="mb-0 card-title">{t("E-manifest Information")}</span>
					</div>

					<div className="card-body">
						<Row>
							<Col md={4}>
								<b>{t("E-manifest Number:")} </b>
								{t(emanifestInfo?.generatedEmanifestID)}
							</Col>
							<Col md={4}>
								<b>{t("E-manifest Creation Date/Time:")} </b>
								{t(emanifestInfo?.creationDate)}
							</Col>
							<Col md={4}>
								<b>{t("E-manifest Status:")} </b>
								{t(emanifestInfo?.eManifestStatus)}
							</Col>
						</Row>
					</div>
				</div>
				<div className="mb-2 card border">
					<div className="card-header bg-default">
						<span className="mb-0 card-title">{t("Waste Generator")}</span>
					</div>

					<div className="card-body">
						{/* <Row>
							<Col md={4}>
								<b>{t("Entity Name:")} </b>
								{t(emanifestInfo?.wasteGenerator?.firstName)}
							</Col>
							<Col md={4}>
								<b>{t("Unified National Number:")} </b>
								{t(emanifestInfo?.wasteGenerator?.unifiedNationalNumber)}
							</Col>
							<Col md={4}>
								<b>{t("Generator Type:")} </b>
								{t(emanifestInfo?.wasteGenerator?.type)}
							</Col>
						</Row>
						<Row>
							<Col md={4}>
								<b>{t("Address:")} </b>
								{t(emanifestInfo?.wasteGenerator?.address)}
							</Col>
							<Col md={4}>
								<b>{t("National ID / Iqama ID:")} </b>
								{t(emanifestInfo?.wasteGenerator?.nationalId)}
							</Col>
							<Col md={4}>
								<b>{t("Mobile Number:")} </b>
								{emanifestInfo?.wasteGenerator?.mobileNumber}
							</Col>
						</Row> */}
						<Row>
							<Col md={4}>
								<b>{t("Entity Name:")} </b>
								{t(emanifestInfo?.wasteGeneratorDelegate?.companyName)}
							</Col>
							<Col md={4}>
								<b>{t("Unified National Number:")} </b>
								{t(emanifestInfo?.wasteGeneratorDelegate?.unifiedNationalNumber)}
							</Col>
							{/* <Col md={4}>
								<b>{t("Generator Representative Entity Type:")} </b>
								{t(emanifestInfo?.wasteGeneratorDelegate?.type)}
							</Col> */}
						</Row>
						{/* <Row>
							<Col md={4}>
								<b>{t("Address :")} </b>
								{t(emanifestInfo?.wasteGeneratorDelegate?.address)}
							</Col>
						</Row> */}
						<Row>
							<Col md={4}>
								<b>{t("Company Representative Name:")} </b>
								{t(emanifestInfo?.wasteGeneratorDelegate?.firstName)}
							</Col>
							<Col md={4}>
								<b>{t("National ID / Iqama ID:")} </b>
								{t(emanifestInfo?.wasteGeneratorDelegate?.nationalId)}
							</Col>
							<Col md={4}>
								<b>{t("Mobile Number:")} </b>
								{t(emanifestInfo?.wasteGeneratorDelegate?.mobileNumber)}
							</Col>
						</Row>
						<Row>
							<Col md={4}>
								<b>{t("Project License Number:")} </b>
								{t(emanifestInfo?.wasteGenerationProject ? JSON.parse(emanifestInfo?.wasteGenerationProject).licenseNumber : "")}
							</Col>
							<Col md={4}>
								<b>{t("Project Site Address:")} </b>
								{t(emanifestInfo?.wasteGenerationProject ? JSON.parse(emanifestInfo?.wasteGenerationProject).siteAddress : "")}
							</Col>
							<Col md={4}>
								<b>{t("Estimated Weight of the Generated Waste (KG):")} </b>
								{t(emanifestInfo?.wasteGenerationProject ? JSON.parse(emanifestInfo?.wasteGenerationProject).estimatedTotalWeight : "")}
							</Col>
						</Row>
						<Row>
							<Col md={4}>
								<b>{t("Site Location:")} </b>
								<a href={`https://www.google.com/maps?q=${emanifestInfo?.latitudePickupSiteGPS},${emanifestInfo?.longitudePickupSiteGPS}`} target="_blank" rel="noopener noreferrer">
									{t("Go to the site")}
								</a>
							</Col>
						</Row>
					</div>
				</div>

				<div className="mb-2 card border">
					<div className="card-header bg-default">
						<span className="mb-0 card-title">{t("Waste Transporter")}</span>
					</div>

					<div className="card-body">
						<Row>
							<Col md={4}>
								<b>{t("Company Name:")} </b>
								{t(emanifestInfo?.wasteTransporter?.companyName)}
							</Col>
							<Col md={4}>
								<b>{t("Unified National Number:")} </b>
								{emanifestInfo?.wasteTransporter?.unifiedNationalNumber}
							</Col>
							{/* <Col md={4}>
								<b>{t("Transporter Email:")} </b>
								{emanifestInfo?.wasteTransporter?.email}
							</Col> */}
							<Col md={4}>
								<b>{t("MWAN License Number:")} </b>
								{t(emanifestInfo?.wasteTransporter?.MWANLicenseNumber)}
							</Col>
						</Row>
						{/* <Row>
							<Col md={4}>
								<b>{t("Transporter Phone Number:")} </b>
								{emanifestInfo?.wasteTransporter?.phone}
							</Col>
							<Col md={4}>
								<b>{t("Address:")} </b>
								{emanifestInfo?.wasteTransporter?.address}
							</Col>
						</Row> */}
						<Row>
							<Col md={4}>
								<b>{t("Transporter Representative Name:")} </b>
								{t(emanifestInfo?.transporterRepresentative?.firstName)} {t(emanifestInfo?.transporterRepresentative?.lastName)}
							</Col>
							<Col md={4}>
								<b>{t("National ID / Iqama ID:")} </b>
								{t(emanifestInfo?.transporterRepresentative?.nationalId)}
							</Col>
							<Col md={4}>
								<b>{t("Mobile Number:")} </b>
								{t(emanifestInfo?.transporterRepresentative?.mobilePhone)}
							</Col>
						</Row>
						<Row>
							<Col md={4}>
								<b>{t("Truck Driver Name:")} </b>
								{t(emanifestInfo?.truckDriver?.firstName)} {t(emanifestInfo?.truckDriver?.lastName)}
							</Col>
							<Col md={4}>
								<b>{t("National ID / Iqama ID:")} </b>
								{t(emanifestInfo?.truckDriver?.nationalId)}
							</Col>
							<Col md={4}>
								<b>{t("Mobile Number:")} </b>
								{t(emanifestInfo?.truckDriver?.mobilePhone)}
							</Col>
						</Row>
						<Row>
							<Col md={4}>
								<b>{t("License Plate:")} </b>
								{t(emanifestInfo?.truckInfo?.plateNumber)}
							</Col>
							<Col md={4}>
								<b>{t("Vehicle Type:")} </b>
								{t(emanifestInfo?.truckInfo?.type)}
							</Col>
							<Col md={4}>
								<b>{t("Vehicle Tare Weight (KG):")} </b>
								{t(emanifestInfo?.truckInfo?.tareWeight)}
							</Col>
						</Row>
						{/* <Row>
							<Col md={4}>
								<b>{t("Transporter Signature:")} </b>
								{emanifestInfo?.transporterRepresentative?.firstName} {emanifestInfo?.transporterRepresentative?.lastName}
							</Col>
							<Col md={4}>
								<b>{t("E-manifest Submission Date/Time:")} </b>
								{emanifestInfo?.transporterSignatureDateTime}
							</Col>
						</Row> */}
					</div>
				</div>

				<div className="mb-2 card border">
					<div className="card-header bg-default">
						<span className="mb-0 card-title">{t("Waste Receiving Facility")}</span>
					</div>

					<div className="card-body">
						<Row>
							<Col md={4}>
								<b>{t("Company Name:")} </b>
								{t(emanifestInfo?.facilityIdComponent ? JSON.parse(emanifestInfo?.facilityIdComponent).entityName.split("-")[0] : "")}
							</Col>
							<Col md={4}>
								<b>{t("Unified National Number:")} </b>
								{t(emanifestInfo?.facilityIdComponent ? JSON.parse(emanifestInfo?.facilityIdComponent).unn : "")}
							</Col>
							{/* <Col md={4}>
								<b>{t("Receiving Facility Email:")} </b>
								{emanifestInfo?.facilityInformation?.email}
							</Col> */}
							<Col md={4}>
								<b>{t("MWAN License Number:")} </b>
								{t(emanifestInfo?.facilityIdComponent ? JSON.parse(emanifestInfo?.facilityIdComponent).mwanLicenseNumber : "")}
							</Col>
						</Row>
						{/* <Row>
							<Col md={4}>
								<b>{t("Receiving Facility Mobile Number:")} </b>
								{emanifestInfo?.facilityInformation?.phoneNumber}
							</Col>
							<Col md={4}>
								<b>{t("Address:")} </b>
								{emanifestInfo?.facilityInformation?.address}
							</Col>
						</Row> */}
						<Row>
							<Col md={4}>
								<b>{t("Receiving Facility Representative Name:")} </b>
								{emanifestInfo?.facilityAdminName ? t(JSON.parse(emanifestInfo?.facilityAdminName).fullName) : ""}
							</Col>
							<Col md={4}>
								<b>{t("National ID / Iqama ID:")} </b>
								{emanifestInfo?.facilityAdminName ? t(JSON.parse(emanifestInfo?.facilityAdminName).idNumber) : ""}
							</Col>
							<Col md={4}>
								<b>{t("Mobile Number:")} </b>
								{emanifestInfo?.facilityAdminName ? t(JSON.parse(emanifestInfo?.facilityAdminName).mobileNumber) : ""}
							</Col>
						</Row>

						{/* <Row>
							<Col md={12}>
								<b>{t("Waste Management Method:")} </b>
								{emanifestInfo?.WasteManagementMethod}
							</Col>
						</Row>
						<Row>
							<Col md={4}>
								<b>{t("Receiving Facility Signature:")} </b>
								{emanifestInfo?.facilityId}
							</Col>
							<Col md={4}>
								<b>{t("E-manifest Submission Date/Time:")} </b>
								{emanifestInfo?.wasteTransporter?.recivingFacilityDateTime}
							</Col>
						</Row> */}
					</div>
				</div>

				<div className="mb-2 card border">
					<div className="card-header bg-default">
						<span className="mb-0 card-title">{t("Information about the Waste")}</span>
					</div>

					<div className="card-body">
						<Row>
							<Col md={4}>
								<b>{t("Date the shipment was lifted") + ":"} </b>
								{t(emanifestInfo?.eManifestSubmissionDateTimePickUp)}
							</Col>
							<Col md={4}>
								<b>{t("Shipment unloading date") + ":"} </b>
								{t(emanifestInfo?.eManifestSubmissionDateTimeDropOff)}
							</Col>
						</Row>

						<Row>
							<Col md={4}>
								<b>{t("Waste Pickup Site Address:")} </b>
								{emanifestInfo?.waste?.pickupSiteAddress}
							</Col>
							<Col md={4}>
								<b>{t("Waste Pickup Site Location:")} </b>
								<a
									href={`https://www.google.com/maps?q=${emanifestInfo?.latitudeTransporterPickUp},${emanifestInfo?.longitudeTransporterPickUp}`}
									target="_blank"
									rel="noopener noreferrer"
								>
									{t("Go to the site")}
								</a>
							</Col>
							<Col md={4}>
								<b>{t("Container Type") + ":"} </b>
								{t(emanifestInfo?.containerType ? JSON.parse(emanifestInfo?.containerType).value : "")}
							</Col>
						</Row>
						<Row>
							<Col md={4}>
								<b>{t("Number of Containers") + ":"} </b>
								{t(emanifestInfo?.numberOfContainers)}
							</Col>
							<Col md={4}>
								<b>{t("Weight of the Received Waste (KG):")} </b>
								{t(emanifestInfo?.weightOfTheReceivedWasteKg)}
							</Col>
							<Col md={4}>
								<b>{t("Waste Load Weight (KG):")} </b>
								{t(emanifestInfo?.netWeightKg)}
							</Col>
						</Row>
						{/* <Row>
							<Col md={12}>
								<b>{t("Waste Description:")} </b>
								{emanifestInfo?.waste?.description}
							</Col>
						</Row>
						<Row>
							<Col md={4}>
								<b>{t("Generator Signature:")} </b>
								{emanifestInfo?.wasteGenerator?.lastName}
							</Col>
							<Col md={4}>
								<b>{t("E-manifest Submission Date/Time:")} </b>
								{emanifestInfo?.creationDate}
							</Col>
						</Row> */}
					</div>
				</div>
			</div>
		</Container>
	);
};

export default EmanifestPreview;
